import * as Sentry from "@sentry/node";
import { createServerFn } from "@tanstack/react-start";
import { useSession } from "@tanstack/react-start/server";
import { graphql } from "react-relay";
import { commitMutation, Environment } from "relay-runtime";
import { z } from "zod";
import { relayNetwork } from "@/relay/network";
import { type SessionData, sessionOptions } from "@/utils/getSession";
import type { loginMutation } from "./__generated__/loginMutation.graphql";

export const login = createServerFn({ method: "POST" })
	.inputValidator(
		z.object({
			username: z.string().email(),
			code: z.string().optional(),
			password: z.string().min(1),
			as: z.string().optional(),
		}),
	)
	.handler(async ({ data }): Promise<Partial<SessionData>> => {
		try {
			// biome-ignore lint/correctness/useHookAtTopLevel: this is not a hook
			const session = await useSession(sessionOptions);

			return new Promise((resolve, reject) => {
				commitMutation<loginMutation>(
					new Environment({
						network: relayNetwork,
					}),
					{
						mutation: graphql`
       	    mutation loginMutation($payload: SignInInput!) {
       					signIn(payload: $payload) {
       					  accessToken
       					  refreshToken
       					  expiresIn
       					  user {
       			        id
                    email
                    username
                    displayName
                    avatarFallback
                    photo {
                      signedUrl
                    }
                    rawID
       					  }
      				}
       			}
       	`,
						variables: {
							payload: {
								username: data.username,
								invite: data.code,
								password: data.password,
								as: data.as?.length ? data.as : undefined,
							},
						},
						onCompleted: (response, errors) => {
							if (errors?.length) {
								reject(new Error(errors[0].message));
								return;
							}
							session
								.update({
									...session.data,
									isLoggedIn: true,
									accessToken: response.signIn.accessToken,
									refreshToken: response.signIn.refreshToken,
									user: response.signIn.user,
									expiresAt: new Date(
										Date.now() +
											response.signIn.expiresIn * 1000 -
											15 * 60 * 1000,
									).toISOString(),
								})
								.then(() => resolve(session.data))
								.catch(reject);
						},
						onError: (
							err: Error & { source?: { errors?: { message: string }[] } },
						) => {
							Sentry.captureException(err);

							if ("source" in err && err.source?.errors?.[0]) {
								reject(new Error(err.source?.errors?.[0].message));
							} else {
								reject(err);
							}
						},
					},
				);
			});
		} catch (err) {
			Sentry.captureException(err);

			throw err;
		}
	});
